import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import {
  BatchPeekHoverHintBody,
  DevicePeekHoverHintBody,
  ShipmentPeekHoverHintBody,
} from "./entityPeekBodies";
import {
  batchPeekQueryKey,
  devicePeekNeedsLookup,
  devicePeekQueryKey,
  ENTITY_PEEK_STALE_MS,
  fetchBatchPeekContext,
  fetchDevicePeekContext,
  fetchSalesOrderPeekContext,
  fetchShipmentPeekContext,
  salesOrderPeekQueryKey,
  shipmentPeekQueryKey,
} from "./entityPeekFetch";
import type { BatchPeekContext, DevicePeekContext, ShipmentPeekContext } from "./entityPeekTypes";
import { SalesOrderPeekHoverHintBody, type SalesOrderPeekContext } from "./SalesOrderPeekHoverHintBody";

function EntityPeekLoading({ label }: { label: string }): JSX.Element {
  return (
    <p className="flex items-center gap-1.5 text-xs text-slate-600">
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      {label}
    </p>
  );
}

function EntityPeekLoadError({ label }: { label: string }): JSX.Element {
  return <p className="text-xs text-slate-600">{label}</p>;
}

export function BatchDetailHoverHint({
  batchId,
  context,
  emptyMessage = "Batch details unavailable.",
}: {
  batchId: number;
  context?: BatchPeekContext;
  emptyMessage?: string;
}): ReactNode {
  const detailQuery = useQuery({
    queryKey: batchPeekQueryKey(batchId),
    queryFn: () => fetchBatchPeekContext(batchId),
    staleTime: ENTITY_PEEK_STALE_MS,
    enabled: context == null,
  });

  if (context != null) {
    return <BatchPeekHoverHintBody context={context} emptyMessage={emptyMessage} />;
  }

  if (detailQuery.isLoading) {
    return <EntityPeekLoading label="Loading batch details…" />;
  }

  if (detailQuery.isError) {
    return <EntityPeekLoadError label="Could not load batch preview." />;
  }

  return <BatchPeekHoverHintBody context={detailQuery.data} emptyMessage={emptyMessage} />;
}

export function ShipmentDetailHoverHint({
  shipmentId,
  context,
  emptyMessage = "No shipment details.",
}: {
  shipmentId: number;
  context?: ShipmentPeekContext;
  emptyMessage?: string;
}): ReactNode {
  const detailQuery = useQuery({
    queryKey: shipmentPeekQueryKey(shipmentId),
    queryFn: () => fetchShipmentPeekContext(shipmentId),
    staleTime: ENTITY_PEEK_STALE_MS,
    enabled: context == null,
  });

  if (context != null) {
    return <ShipmentPeekHoverHintBody context={context} emptyMessage={emptyMessage} />;
  }

  if (detailQuery.isLoading) {
    return <EntityPeekLoading label="Loading shipment details…" />;
  }

  if (detailQuery.isError) {
    return <EntityPeekLoadError label="Could not load shipment preview." />;
  }

  return <ShipmentPeekHoverHintBody context={detailQuery.data} emptyMessage={emptyMessage} />;
}

export function SalesOrderDetailHoverHint({
  salesOrderId,
  context,
  emptyMessage = "No sales order context.",
}: {
  salesOrderId: number;
  context?: SalesOrderPeekContext;
  emptyMessage?: string;
}): ReactNode {
  const detailQuery = useQuery({
    queryKey: salesOrderPeekQueryKey(salesOrderId),
    queryFn: () => fetchSalesOrderPeekContext(salesOrderId),
    staleTime: ENTITY_PEEK_STALE_MS,
    enabled: context == null,
  });

  if (context != null) {
    return <SalesOrderPeekHoverHintBody context={context} emptyMessage={emptyMessage} />;
  }

  if (detailQuery.isLoading) {
    return <EntityPeekLoading label="Loading sales order details…" />;
  }

  if (detailQuery.isError) {
    return <EntityPeekLoadError label="Could not load sales order preview." />;
  }

  return <SalesOrderPeekHoverHintBody context={detailQuery.data} emptyMessage={emptyMessage} />;
}

/** Device serial peek: use inline context when the page already loaded it; otherwise fetch manufacturing device-lookup. */
export function DeviceDetailHoverHint({ context }: { context: DevicePeekContext }): ReactNode {
  const needsLookup = devicePeekNeedsLookup(context);
  const detailQuery = useQuery({
    queryKey: devicePeekQueryKey(context.serial),
    queryFn: () => fetchDevicePeekContext(context.serial),
    staleTime: ENTITY_PEEK_STALE_MS,
    enabled: needsLookup && context.serial.trim() !== "",
  });

  if (!needsLookup) {
    return <DevicePeekHoverHintBody context={context} />;
  }

  if (detailQuery.isLoading) {
    return <EntityPeekLoading label="Loading device details…" />;
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return <DevicePeekHoverHintBody context={context} />;
  }

  return <DevicePeekHoverHintBody context={detailQuery.data} />;
}
